/**
 * Medical history from the patient app (patient-platform tasks/profile-guardians-history.md): allergies, medical
 * history, medications and doctors the patient added to their profile, filed into this practice's lists item by item
 * in the Inbox (`patient-app-history`), and the "Also add to" choice when a shared entry is added to the timeline.
 */

export type HistoryDecision = 'accept' | 'reject';

export interface PatientAppHistoryItem {
  itemId: string;
  text: string;
  /** The item's own fields (allergy and reaction, surgery and year, ...), as the patient entered them. */
  item?: Record<string, unknown>;
  /** The patient's record already holds it: it needs no decision and is never filed twice. */
  alreadyRecorded: boolean;
}

export interface PatientAppHistoryProposal {
  id: string;
  status: 'pending' | 'resolved';
  receivedAt: string;
  patientId: string;
  lists: { list: string; label: string; items: PatientAppHistoryItem[] }[];
  /** Once reviewed: item id -> filed | already_recorded | not_filed. */
  decisions: Record<string, string> | null;
  resolvedAt: string | null;
}

export const HISTORY_OUTCOME_LABELS: Record<string, string> = {
  filed: 'Filed',
  already_recorded: 'Already in the record',
  not_filed: 'Not filed',
};

/** The items still waiting for accept or reject, as the reviewer reads them. */
export function undecidedHistoryItems(proposal: PatientAppHistoryProposal, decisions: Record<string, HistoryDecision | undefined>): string[] {
  return proposal.lists.flatMap((l) => l.items).filter((i) => !i.alreadyRecorded && !decisions[i.itemId]).map((i) => i.text);
}

const ENTRY_LIST_LABELS: Record<string, string> = { allergies: 'Allergies', medicalHistory: 'Medical history', medicationHistory: 'Medications' };

/** "Also add to Allergies" for a shared entry whose kind belongs in a list; null when it belongs in none. */
export function alsoAddLabel(listTarget: string | null | undefined): string | null {
  const label = listTarget ? ENTRY_LIST_LABELS[listTarget] : undefined;
  return label ? `Also add to ${label}` : null;
}
